import ComponentDefinition from "./ComponentDefinition";
import {isStyleClassComponentClass} from "./StyleClassComponentClass";
import {isTagNameComponentClass} from "./TagNameComponentClass";
import {isNamespaceUriComponentClass} from "./NamespaceUriComponentClass";
import deepCopy from "../scripts/deepCopy";
import deepFreeze from "../scripts/deepFreeze";
import splitAtWhitespace from "../scripts/splitAtWhitespace";
import ComponentManager from "./ComponentManager";
import ComponentClass from "./ComponentClass";

type ComponentOptionsInterfaceValue = string | number | boolean | object | Date | Array<string|number|boolean|Date> | ComponentOptionsInterface;
export interface ComponentOptionsInterface {[name: string]: ComponentOptionsInterfaceValue}

type ParserFunction<E> = (value: string | null, attributeName: string) => E | null | undefined;
type ValuePlaceholder<E extends ComponentOptionsInterfaceValue = ComponentOptionsInterfaceValue> = {
	___isNull: boolean,
	___parser: ParserFunction<E>;
};

// TODO @Michael: Add Node / Element. And for attributes run a query -- but with what context? Maybe use ":scope" to say from self? What about for a closest() call?
/**
 * Values to use as placeholders so we can infer type while causing
 * an option to return null when accessed.
 */
export const ComponentOptionsValuePlaceholder = deepFreeze({
	string: makePlaceholder(value => value),
	number: makePlaceholder((number, attributeName) => {
		const valAsNumber = Number(number);
		if (Number.isNaN(valAsNumber)) {
			console.error(`Error parsing the "${attributeName}" attribute. Could not parse value, "${number}". Was expecting a number but got NaN when parsed.`);
			return undefined;
		}
		return valAsNumber;
	}),
	boolean: makePlaceholder(value => value !== null && value !== undefined && "true" === value.trim().toLowerCase()),
	object: makePlaceholder(parseObjectOrArray("object")),
	array: makePlaceholder(parseObjectOrArray<string[]>("array")),
	date: makePlaceholder(value => value !== null ? new Date(value) : undefined)
});
const OrderedApplicationTests: Array<[(obj: any) => boolean, keyof typeof ComponentOptionsValuePlaceholder]> = [
	[obj => obj instanceof Date, "date"],
	[Array.isArray, "array"],
	[() => true, "object"] // This must always be the last item in the list because the plain object parser is the fallback
];

function isValuePlaceholder(value: any): value is ValuePlaceholder {
	return value !== null && value !== undefined && value.___isNull !== undefined && typeof value.___parser === "function";
}

function makePlaceholder<E extends ComponentOptionsInterfaceValue>(parser: ParserFunction<E>) {
	return {
		optional: {___isNull: true,  ___parser: parser} as unknown,
		required: {___isNull: false, ___parser: parser} as unknown,
		___parser: parser
	} as {optional: E, required: E}; // We're purposely excluding parser in order to prevent type-based suggestions on ComponentOptionsValuePlaceholder's elements
                                     // from seeing that ___parser exists
}

function parseObjectOrArray<E = object>(returnType: string) {
	return (value: string | null, attributeName: string): E | undefined => {
		try {
			if (value !== null && isJson(value)) {
				return JSON.parse(value);
			}
			return splitAtWhitespace(value) as any;
		} catch (error) {
			console.error(`Error parsing the "${attributeName}" attribute. Could not parse value, "${value}". Was expecting a type of "${returnType}".`, error);
			return undefined;
		}
	};
}
function isJson(value: string): boolean {
	const re = /^[\s]*[{\[]/; // First character after any whitespace is a { or [
	return re.test(value);
}

/**
 * TODO @Michael: Document purpose, options and example use. Notes:
 *
 	<div class="inlineMenu"
		data-inlineMenu-testIsObj='{"hi" : "hola", "another" : "TODO first override"}'
		data-inlineMenu-testIsObj.another="TODO Test value for another"
	> ...</div>

 	private static defaultOptions = {
		isReadyStyleClass: "inlineMenu--isReady",
		expandedStyleClass: "inlineMenu--isExpanded",
		controlIsReadyStyleClass: "inlineMenu__control--isReady",
		testIsObj: {hi: "hello", another: "yup", oneMore: {}}
	};
	protected options: ComponentOptions<typeof InlineMenu.defaultOptions>;
 	...
 	this.options = new ComponentOptions(this, this.rootElement, InlineMenu.defaultOptions);
 	....
 	const options = this.options.getValues();
 	options.isReadyStyleClass
 *
 * And use this.options.extend() when extending. For example, the super sets up the initial instance
 * and then descents would use extend();
 *
 *
 * The merged properties are cached. If the attributes change, you need to rebuild by calling clearCache();
 * Defaults are not cleared, they are merged as added. The cache is the merger of the defaults, and attributes.
 *
 * Call destroy() when you component is being destroyed.
 *
 * The defaults must contain all possible properties you want to use.
 *
 * Attribute format is:
	 * For style class components: data-componentStyleClass-camelCasePropName.camelCasePropName
	 * For non-namespaced tag named components: data-componentTagName-camelCasePropName.camelCasePropName
	 * For namespaced tag named components: ns:componentTagName-camelCasePropName
 * Note: Use of namespaced tag name components is not recommended due to browsers giving XML a hard time.
 * 		TODO: If we were to use the namespaced tag names .... we may want to change the format of the attribute. Just "camelCasePropName" may be good actually.
 *
 * Note: The one really limitation to be aware of is that your default must contain all properties that may be on attributes, and the type must be the same.
 *          You can't set your default to "undefined" when the attribute may provide a "string".
 *
 * Options returned via getOptions are frozen -- You can not change the values.
 *
 * TODO Do arrays merge or not? Add an option?
 */
export default class ComponentOptions<Options extends ComponentOptionsInterface> {
	private readonly defaultsOptions: Options;

	private cacheMergedOptions: Options | undefined;

	constructor(
		private readonly componentInstance: object,
		private readonly rootElement: Element,
		defaults: Options,
		overrides?: Partial<Options>
	) {
		this.defaultsOptions = deepCopy([defaults, overrides]);
	}

	static isUndefined(x: any): x is undefined {
		return x === undefined;
	}

	/**
	 * Extends this the instance of ComponentOptions and returns a new instance of ComponentOptions,
	 * unless `defaults` argument is undefined (or excluded) than this current instance is returned.
	 * @param defaults
	 */
	extend<O2 extends ComponentOptionsInterface>(defaults?: O2): ComponentOptions<Options & O2> {
		if (defaults === undefined) {
			return this as unknown as ComponentOptions<Options & O2>; // I don't like the casting, but I can't find another option that works in the ways I want it available to extend components during construction.
		}
		const newDefaults = deepCopy([this.defaultsOptions, defaults]);
		return new ComponentOptions(
			this.componentInstance,
			this.rootElement,
			newDefaults
		);
	}

	getValues(): Readonly<Options> {
		if (this.cacheMergedOptions === undefined) {
			const deepMerged = deepCopy([this.defaultsOptions, this.getAttributeOptions()]);
			this.cacheMergedOptions = deepFreeze(deepMerged, (value, key) => {
				if (key === undefined) {
					return value; // The undefined key is the root object being processed.
				}
				// Convert the values that use ComponentOptionsValuePlaceholder to null before freezing.
				if (
					value !== undefined && value !== null
					&& (value as any).___parser !== undefined
				) {
					if ((value as any).___isNull === false) {
						throw `Required property ("${key}") could not be resolved, while attempting to load \
component options for component: ${ComponentManager.getComponentNameForLogging(this.componentInstance)}`;
					}
					return null;
				}
				return value;
			});
		}
		return this.cacheMergedOptions;
	}

	private getAttributeOptions(): Options | undefined {
		if (!this.rootElement.hasAttributes()) {
			return;
		}

		const attributeOptions = Object.create(null);

		// Determine attribute prefix and namespace.
		let {attributePrefix, namespaceUri} = this.getAttributePrefixAndNamespaceUri();

		// Sort attributes by putting those with more "." later in the list.
		attributePrefix = attributePrefix.toLowerCase();
		const attributes = Array.from(this.rootElement.attributes);
		attributes.sort((a, b) => {
			const aLen = a.localName.split('.').length;
			const bLen = b.localName.split('.').length;
			return aLen - bLen;
		});

		// Process each attribute
		attributes.forEach(attribute => {
			const localName = attribute.localName.toLowerCase(); // Browsers should return in all lowercase but lets make sure.
			if (
				localName.indexOf(attributePrefix) === 0
				&& (namespaceUri === null || attribute.namespaceURI === namespaceUri)
			) {
				let defaultOptionValue: any = this.defaultsOptions;
				let attributeOptionValue: any = attributeOptions;

				const propChain = localName.substring(attributePrefix.length).split('.');
				for (let j = 0;;) {
					let propName = propChain[j];

					// Since attribute names are lowercase, we need to figure out what the correct case is by looking at the defaults
					let correctPropName;
					if (Object.prototype.hasOwnProperty.call(defaultOptionValue, propName!)) { // defaultOptionValue might not have a prototype
						correctPropName = propName;
					} else {
						const ownDefaultProp = Object.keys(defaultOptionValue);
						for (let i=0; i < ownDefaultProp.length; i++) {
							const prop = ownDefaultProp[i];
							if (propName === prop!.toLowerCase()){
								correctPropName = prop;
								break; // Found it!
							}
						}
					}
					if (correctPropName === undefined) {
						throw new Error(`Processing "${localName}" attribute, but can not find default
							option for property "${propName}". A default must be provided so a type can be inferred.`);
					}

					// Update defaultOptionValue to be where we are in the chain of properties
					defaultOptionValue = defaultOptionValue[correctPropName];

					// Update attributeOptionValue to be where we are in the chain of properties.
					// If the spot is undefined create an an empty object
					// Unless we are at the end of the property chain, then we process the value.
					if (++j < propChain.length) {
						if (attributeOptionValue[correctPropName] === undefined) {
							attributeOptionValue[correctPropName] = attributeOptionValue = Object.create(null);
						} else {
							attributeOptionValue = attributeOptionValue[correctPropName];
						}
					} else {
						const value = ComponentOptions.convertAttributeValueToExpectedType(defaultOptionValue, attribute);
						if (value !== undefined) {
							attributeOptionValue[correctPropName] = typeof value === "object" ? deepCopy([attributeOptions[correctPropName], value]) : value;
						}

						break; // Out of the prop chain loop.
					}
				}
			}
		});

		return attributeOptions;
	}

	getAttributePrefixAndNamespaceUri(): {attributePrefix: string, namespaceUri: string | null} {
		return ComponentOptions.getAttributePrefixAndNamespaceUri(this.componentInstance);
	}
	static getAttributePrefixAndNamespaceUri(componentInstance: object): {attributePrefix: string, namespaceUri: string | null} {
		const componentClass = ComponentDefinition.getComponentClass(componentInstance);
		// This call structure allows for tail-call optimizations, conveniently enough.
		if (isStyleClassComponentClass(componentClass)) {
			// Format @data-componentStyleClass-camelCasePropName.camelCasePropName=""
			return {attributePrefix: `data-${componentClass.componentStyleClass}-`, namespaceUri: null};
		} else if (isNamespaceUriComponentClass(componentClass)) {
			// Format @ns:componentTagName-camelCasePropName.camelCasePropName=""
			return {attributePrefix: `${componentClass.componentTagName}-`, namespaceUri: componentClass.componentNamespaceUri};
		} else if (isTagNameComponentClass(componentClass)) {
			// Format @data-componentTagName-camelCasePropName.camelCasePropName=""
			return {attributePrefix: `data-${componentClass.componentTagName}-`, namespaceUri: null};
		} else {
			throw new Error(`Class for instance does not implement one of the expected component class interfaces.`);
		}
	}

	/**
	 * This method is usable for TagNameComponentClass and StyleClassComponentClass implementations.
	 * If you are using a NamespaceUriComponentClass implementation you need to use getAttributePrefixAndNamespaceUri().
	 */
	getAttributeName(optionName: string): string {
		return ComponentOptions.getAttributeName(this.componentInstance, optionName);
	}
	static getAttributeName(componentInstance: object, optionName: string): string {
		return ComponentOptions.getAttributePrefixAndNamespaceUri(componentInstance).attributePrefix + optionName;
	}



	/**
	 * Returns the `nodeValue` of `attribute` converted to the same type as `defaultValue`.
	 * If there is an error converting it is logged to the console and undefined is returned.
	 * A a 'null' may be returned if ComponentOptionsValuePlaceholder values are used but not replaced.
	 * @param defaultValue
	 * @param attribute
	 * @private
	 */
	private static convertAttributeValueToExpectedType(defaultValue: ComponentOptionsInterfaceValue, attribute: Attr): ComponentOptionsInterfaceValue | null | undefined {
		// Check if default value is one of ComponentOptionsValuePlaceholder
		if (isValuePlaceholder(defaultValue)) {
			return defaultValue.___parser(attribute.nodeValue, attribute.localName);
		} else {
			const returnType = typeof defaultValue;
			let typeDefinition = undefined;
			if (returnType === "object") {
				for (let i = 0; i < OrderedApplicationTests.length; i++) {
					let orderedApplicationTest = OrderedApplicationTests[i]!;
					if (orderedApplicationTest[0](defaultValue)) {
						typeDefinition = ComponentOptionsValuePlaceholder[orderedApplicationTest[1]];
						break;
					}
				}
			} else {
				typeDefinition = (ComponentOptionsValuePlaceholder as any)[returnType];
			}
			if (typeDefinition !== null && typeDefinition !== undefined) {
				return typeDefinition.___parser(attribute.nodeValue, attribute.localName);
			}

			console.error(`Error parsing the "${attribute.localName}" attribute. The value's type, "${returnType}", is not supported or recognized so value could not be converted.`);
			return undefined;
		}
	}
}
